import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ImArrowLeft2 } from 'react-icons/im';

import logoImg from '../../assets/logo-cinco-grey.svg';

import { Container, Content, Header, Footer, Background } from './styles';
import support from '../../utils/support';
import RegisterStartupModal from '../../modals/RegisterStartupModal';
import { Email } from './Email';
import { UserData } from './UserData';
import { Cellphone } from './Cellphone';
import { Address } from './Address';
import { CreditCardPayment } from './CreditCardPayment';
import { PaymentMethodEnum } from '../../enums/PaymentMethodEnum';
import { ApprovedPayment } from './ApprovedPayment';
import { Subscription } from './Subscription';
import { FinishedPayment } from './FinishedPayment';
import { PixPaymentCPF } from './PixPaymentCPF';
import { PixPayment } from './PixPayment';
import { PaymentError } from './PaymentError';
import { SubscriptionInfo } from './SubscriptionInfo';
import { Payment } from './Payment';
import { useUser } from '../../hooks/user';

export interface ComponentUserRegisterProps {
  nextPage: (userIsRegistered?: boolean, userStatus?: string) => void;
  backPage?: () => void;
  handleScreenSelection: (value: PaymentMethodEnum) => void;
  handlePaymentError: () => void;
  handleTryAgain: () => void;
}

export const stepLabels = [
  'Assinatura',
  'Dados de Contato',
  'Endereço',
  'Pagamento',
];

const Register: React.FC = () => {
  const initialValue = {
    SubscriptionInfo: {
      key: 0,
      name: 'SubscriptionInfo',
      component: SubscriptionInfo,
    },
    Subscription: {
      key: 1,
      name: 'Subscription',
      component: Subscription,
    },
    Email: {
      key: 2,
      name: 'Email',
      component: Email,
    },
    UserData: {
      key: 3,
      name: 'UserData',
      component: UserData,
    },
    Cellphone: {
      key: 4,
      name: 'Cellphone',
      component: Cellphone,
    },
    Address: {
      key: 5,
      name: 'Address',
      component: Address,
    },
    Payment: {
      key: 6,
      name: 'Payment',
      component: Payment,
    },
    SelectedPayment: {
      key: 7,
      name: 'SelectedPayment',
      component: CreditCardPayment,
    },
    ApprovedPayment: {
      // credit card flow
      key: 8,
      name: 'ApprovedPayment',
      component: ApprovedPayment,
    },
    PixPayment: {
      key: 8,
      name: 'PixPayment',
      component: PixPayment,
    },
    FinishedPayment: {
      key: 9,
      name: 'FinishedPayment',
      component: FinishedPayment,
    },
    PaymentError: {
      key: 1000,
      name: 'PaymentError',
      component: PaymentError,
    },
  };

  const { setUser } = useUser();

  type OptionsKeys = keyof Options;

  type Options = Partial<typeof initialValue>;

  const [options, setOptions] = useState<Options>(initialValue);

  const [selected, setSelected] = useState<keyof typeof options>(
    Object.keys(initialValue)[0] as keyof typeof options,
  );

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const handleNext = useCallback(() => {
    const steps: Array<[OptionsKeys, Options[OptionsKeys]]> = Object.entries(
      options,
    ) as [OptionsKeys, Options[OptionsKeys]][];

    steps.sort((a, b) => {
      const keyA = a[1]?.key;
      const keyB = b[1]?.key;

      if (keyA === undefined && keyB === undefined) {
        return 0;
      }

      if (keyA === undefined) {
        return -1;
      }

      if (keyB === undefined) {
        return 1;
      }

      return keyA - keyB;
    });

    const selectedOption = options[selected];

    const newIndex = selectedOption ? selectedOption.key + 1 : 0;

    setSelected(steps[newIndex][0]);
  }, [options, selected]);

  const handleBack = useCallback(() => {
    const steps: Array<[OptionsKeys, Options[OptionsKeys]]> = Object.entries(
      options,
    ) as [OptionsKeys, Options[OptionsKeys]][];

    steps.sort((a, b) => {
      const keyA = a[1]?.key;
      const keyB = b[1]?.key;

      if (keyA === undefined && keyB === undefined) {
        return 0;
      }

      if (keyA === undefined) {
        return -1;
      }

      if (keyB === undefined) {
        return 1;
      }

      return keyA - keyB;
    });

    const selectedOption = options[selected];

    const newIndex = selectedOption ? selectedOption.key - 1 : 0;

    if (newIndex < 0) {
      setSelected(steps[0][0]);
    } else {
      setSelected(steps[newIndex][0]);
    }
  }, [options, selected]);

  const handleScreenSelection = useCallback(
    (paymentOption: PaymentMethodEnum) => {
      const creditCardScreen = {
        key: 7,
        name: 'CreditCardPayment',
        component: CreditCardPayment,
      };

      const pixCPFScreen = {
        key: 7,
        name: 'PixPaymentCPF',
        component: PixPaymentCPF,
      };

      const auxOptions = { ...options };

      setUser(prev => ({
        ...prev,
        paymentMethod: paymentOption,
      }));

      if (paymentOption === PaymentMethodEnum.CreditCard) {
        auxOptions.SelectedPayment = creditCardScreen;

        if (!auxOptions.ApprovedPayment) {
          auxOptions.ApprovedPayment = {
            key: 8,
            name: 'ApprovedPayment',
            component: ApprovedPayment,
          };
        }

        setSelected('SelectedPayment');

        delete auxOptions.PixPayment;
      }

      if (paymentOption === PaymentMethodEnum.Pix) {
        auxOptions.SelectedPayment = pixCPFScreen;

        if (!auxOptions.PixPayment) {
          auxOptions.PixPayment = {
            key: 8,
            name: 'PixPayment',
            component: PixPayment,
          };
        }

        setSelected('SelectedPayment');

        delete auxOptions.ApprovedPayment;
      }

      setOptions(auxOptions);
    },
    [options, setUser],
  );

  const handlePaymentError = (): void => {
    setSelected('PaymentError');
  };

  const handleTryAgain = (): void => {
    setSelected('SelectedPayment');
  };

  const selectedComponent = useMemo(() => {
    const selectedOption = options[selected];

    const Component = selectedOption ? selectedOption.component : null;

    if (!Component) {
      return null;
    }

    return (
      <Component
        nextPage={handleNext}
        backPage={handleBack}
        handleScreenSelection={handleScreenSelection}
        handlePaymentError={handlePaymentError}
        handleTryAgain={handleTryAgain}
      />
    );
  }, [options, selected, handleNext, handleBack, handleScreenSelection]);

  useEffect(() => {
    setModalIsOpen(true);
  }, []);

  return (
    <Container>
      {modalIsOpen && (
        <RegisterStartupModal closeModal={() => setModalIsOpen(false)} />
      )}
      <Content
        isWiderVersion={
          selected !== 'FinishedPayment' &&
          selected !== 'PaymentError' &&
          selected !== 'ApprovedPayment'
        }
      >
        <Header>
          <img src={logoImg} alt="Cinco" />

          {selected !== 'SubscriptionInfo' && (
            <button type="button" onClick={handleBack}>
              <ImArrowLeft2 size={25} />
            </button>
          )}
        </Header>

        {selectedComponent}

        <Footer>
          <p>Precisa de ajuda?</p>
          <p>
            Entre em contato com{' '}
            <a href={`mailto:${support.email}`}>
              <b>{support.email}</b>
            </a>
          </p>
        </Footer>
      </Content>

      <Background>
        <div />
      </Background>
    </Container>
  );
};
export default Register;
